<template>
  <div id="contact" class="header-padding bg-light-grey">
    
    <section role="banner" id="banner" class="bg-grey">
      <transition name="fade">
        <div v-show="bannerImage.desktop" class="banner-image ff-mobile-hide" :style="'background-image: url(' + bannerImage.desktop + ');'"></div>
      </transition>
      <transition name="fade">
        <div v-show="bannerImage.mobile" class="banner-image ff-desktop-hide" :style="'background-image: url(' + bannerImage.mobile + ');'"></div>
      </transition>
      
      <div class="banner-content ff ff-ttb ff-align-center ff-mobile-hide" :class="bannerAlignment">
        <h1 
          class="banner-title ff-lg-6 ff-xl-5 ff-xxl-4" 
          :class="bannerColor"
          :style="'margin-bottom:0; margin-top:0;'"
        >{{ pageName }}</h1>
        <div v-if="bannerText" v-html="bannerText" class="banner-text ff-lg-6 ff-xl-5 ff-xxl-4" :class="bannerColor"></div>
      </div>
      
      <div class="banner-content-mobile bg-light-grey ff-desktop-hide ff-text-center horizontal-padding section-padding">
        <h1 
          class="banner-title ff-lg-6 ff-xl-5 ff-xxl-4" 
          :class="bannerColor"
          :style="'margin-bottom:0; margin-top:0;'"
        >{{ pageName }}</h1>
        <div v-if="bannerText" v-html="bannerText" class="banner-text"></div>
      </div>
    </section>
    
    <v-row v-for="(row, r) in rows"
      v-bind:content="row"
      v-bind:index="r"
      v-bind:scrollTo="'#map'"
      v-bind:key="'section-' + r"
    ></v-row>
    
    <section role="map" class="bg-dark-grey">
      <div id="map"></div>
      <div class="directions-button">
        <a href="https://www.google.com/maps/dir//2+Altona+St,+Bibra+Lake+WA+6163/@-32.0954945,115.7995667,17z/data=!4m9!4m8!1m0!1m5!1m1!1s0x2a329881d4920315:0x10c18f5a462674f!2m2!1d115.8017554!2d-32.0954945!3e0" class="btn bg-cyan" target="_blank">Get Directions</a>
      </div>
    </section>
    
    <section role="enquiry" id="form" class="bg-light-grey ff ff-ttb ff-center horizontal-padding section-padding">
      
      <h2 class="section-heading">Make an Enquiry</h2>
      
      <div id="formContainer" class="max-width-1200 ff-12 ff-md-11">
        
        <transition name="field-slide">
          <div v-if="form.response.message && form.response.status === 'failure'" class="form-error-message ff-text-center">
            <div class="half-padding">
              <p>{{ form.response.message }}</p>
            </div>
          </div>
        </transition>
        
        <transition name="fade">  
          <div v-if="form.response.message && form.response.status === 'success'" class="form-success-message ff-text-center">
            <div class="max-width-900">
              <p class="large">{{ form.response.message }}</p>
            </div>
          </div>
        </transition>
        
        <div id="formBlock" class="content-block" :class="form.response.status === 'success' ? 'hidden' : ''">
          <form id="enquiry-form" class="standard ff-text-left" @submit.prevent="submitForm" method="post">
              
            <div class="form-section">
              <div class="ff-columns">
                <div class="form-block form-block-first-name ff-12 ff-md-6">
                  <label class="label" for="first-name">First name: <span class="required">*</span></label>
                  <input 
                    type="text" 
                    name="first-name" 
                    id="first-name" 
                    v-model="form.firstName"
                  >
                  <transition name="field-slide">
                    <div v-if="form.response.errors.firstName" class="form-error">
                      <span>Please enter your first name.</span>
                    </div>
                  </transition>
                </div>
                
                <div class="form-block form-block-last-name ff-12 ff-md-6">
                  <label class="label" for="last-name">Last name: <span class="required">*</span></label>
                  <input 
                    type="text" 
                    name="last-name" 
                    id="last-name" 
                    v-model="form.lastName"
                  >
                  <transition name="field-slide">
                    <div v-if="form.response.errors.lastName" class="form-error">
                      <span>Please enter your last name.</span>
                    </div>
                  </transition>
                </div>
                
                <div class="form-block form-block-email ff-12 ff-md-6">
                  <label class="label" for="email">Email address: <transition name="fade"><span v-if="conditionalRequiredFields.email" class="required">*</span></transition></label>
                  <input 
                    type="email" 
                    name="email" 
                    id="email" 
                    v-model="form.email"
                  >
                  <transition name="field-slide">
                    <div v-if="form.response.errors.email" class="form-error">
                      <span v-if="form.response.errors.email === 'invalid'">Please enter a valid email address.</span>
                      <span v-if="form.response.errors.email === 'required'">Please enter your email address.</span>
                    </div>
                  </transition>
                </div>
                
                <div class="form-block form-block-phone ff-12 ff-md-6">
                  <label class="label" for="phone">Contact number: <transition name="fade"><span v-if="conditionalRequiredFields.phone" class="required">*</span></transition></label>
                  <input 
                    type="text" 
                    name="phone" 
                    id="phone" 
                    v-model="form.phone"
                  >
                  <transition name="field-slide">
                    <div v-if="form.response.errors.phone" class="form-error">
                      <span v-if="form.response.errors.phone === 'invalid'">Please enter a valid phone number.</span>
                      <span v-if="form.response.errors.phone === 'required'">Please enter your phone number.</span>
                    </div>
                  </transition>
                </div>
                
                <div class="form-block form-block-special ff-12 ff-md-6">
                  <label class="label" :for="form.specialId">{{ form.specialName }}: <span class="required">*</span></label>
                  <input 
                    type="text" 
                    :name="form.specialId" 
                    :id="form.specialId" 
                    v-model="form.special"
                  >
                </div>
              </div>
            </div>
            
            <div class="form-section border-top">
              <div class="ff-columns">
                <div class="form-block form-block-message ff-12">
                  <label class="label" for="message">How can we help?</label>
                  <textarea 
                    class="message" 
                    name="message" 
                    id="message"
                    v-model="form.message"
                  ></textarea>
                </div>
                
                <transition name="field-slide">
                  <div v-if="form.phone && form.email" class="form-block form-block-contact-method ff-12">
                    <div class="label">How should we contact you? <span v-if="conditionalRequiredFields.contactMethod" class="required">*</span></div>
                    <div class="form-block-radio-group ff-columns">
                      
                      <div class="ff-12 ff-md-6 form-block-radio form-block-radio-tile">
                        <input 
                          type="radio" 
                          id="contact-method-email" 
                          value="Email" 
                          v-model="form.contactMethod"
                        >
                        <label for="contact-method-email">
                          <span class="radio-tick"><icon name="ui-tick" color="cyan" /></span>
                          <span>
                            <icon name="icon-mail" color="white" />
                            <span>By Email</span>
                          </span>
                        </label>
                      </div>
                      
                      <div class="ff-12 ff-md-6 form-block-radio form-block-radio-tile">
                        <input 
                          type="radio" 
                          id="contact-method-phone" 
                          value="Phone" 
                          v-model="form.contactMethod"
                        >
                        <label for="contact-method-phone">
                          <span class="radio-tick"><icon name="ui-tick" color="cyan" /></span>
                          <span>
                            <icon name="icon-phone" color="white" />
                            <span>By Phone</span>
                          </span>
                        </label>
                      </div>
                      
                    </div>
                    <transition name="field-slide">
                      <div v-if="form.response.errors.contactMethod" class="form-error">
                        <span>Please select your preferred method of contact.</span>
                      </div>
                    </transition>
                  </div>
                </transition>
                
              </div>
            </div>
            
            <div class="form-section border-top">
              <div class="ff-columns">
              
                <div class="form-block form-block-terms ff-12">
                  <div class="label">Do you agree to the CSBP privacy policy and terms and conditions <span class="required">*</span></div>
                  <div class="form-block-checkbox-group ff-columns">
                    
                    <div class="ff-12 form-block-checkbox">
                      <input 
                        type="checkbox" 
                        id="terms-yes" 
                        v-model="form.terms"
                        true-value="Yes"
                        false-value=""
                      >
                      <label for="terms-yes">
                        <span class="checkbox-tick"><icon name="ui-tick" color="cyan" /></span>
                        <span>Yes, I agree to the CSBP Privacy Policy and Terms and Conditions</span>
                      </label>
                    </div>
                    
                  </div>
                  <transition name="field-slide">
                    <div v-if="form.response.errors.contactMethod" class="form-error">
                      <span>You must agree to our Privacy Policy and Terms and Conditions to proceed with your enquiry.</span>
                    </div>
                  </transition>
                </div>
                
                <div class="form-block form-block-submit ff-12">
                  <button role="submit" class="btn bg-cyan ff-auto">
                    <span>Submit</span>
                    <icon name="ui-chevron-right" color="white" />
                  </button>
                </div>
                
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  
  </div>
</template>

<script>
import Rows from '@/components/Rows.vue'
import gmapsInit from './../gmaps'

// @ is an alias to /src
export default {
  name: "contact",
  components: {
    'v-row': Rows
  },
  data() {
    return {
      banner: {
        size: {
          desktop: '',
          mobile: ''
        }
      },
      form: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: '',
        contactMethod: '',
        terms: false,
        response: {
          status: '',
          message: '',
          error: '',
          errors: []
        },
        special: '',
        specialName: 'Middle name',
        specialId: 'middleName',
        datetime: {
          months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          load: null,
          submit: null
        },
      },
      map: {
        coords: {
          lat: -32.095540,
          lng: 115.802280
        },
        styles: [
          {
            "featureType": "all",
            "elementType": "labels.text.fill",
            "stylers": [
              {
                "color": "#ffffff"
              },
              {
                "weight": "1"
              },
              {
                "lightness": "0"
              }
            ]
          },
          {
            "featureType": "all",
            "elementType": "labels.text.stroke",
            "stylers": [
              {
                "visibility": "on"
              },
              {
                "color": "#b3bdc7"
              },
              {
                "weight": "3.22"
              },
              {
                "gamma": 0.84
              },
              {
                "lightness": "-40"
              }
            ]
          },
          {
            "featureType": "all",
            "elementType": "labels.icon",
            "stylers": [
              {
                "visibility": "off"
              }
            ]
          },
          {
            "featureType": "landscape",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#b3bdc7"
              }
            ]
          },
          {
            "featureType": "poi",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#b3bdc7"
              }
            ]
          },
          {
            "featureType": "poi.park",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#b3bdc7"
              },
              {
                "lightness": "-10"
              }
            ]
          },
          {
            "featureType": "road",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#b3bdc7"
              },
              {
                "lightness": "-23"
              }
            ]
          },
          {
            "featureType": "road.local",
            "elementType": "geometry.fill",
            "stylers": [
              {
                "weight": "0.7"
              }
            ]
          },
          {
            "featureType": "transit",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#b3bdc7"
              },
              {
                "lightness": "-23"
              }
            ]
          },
          {
            "featureType": "water",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#b3bdc7"
              },
              {
                "saturation": "0"
              },
              {
                "lightness": "-65"
              }
            ]
          }
        ]
      },
      pageContent: null,
      loading: false
    }
  },
  computed: {
    apiURL() {
      return this.$store.state.apiURL;
    },
    rows() {
      var rows = [];
      
      if(this.pageContent) {
        rows = this.pageContent.content.rows;
      }
      
      return rows;
    },
    pageName() {
      var pageName = '';
      
      if(this.pageContent) {
        pageName = this.pageContent.name;
      }
      
      return pageName;
    },
    screenSize(){
      var screenSize = this.$store.getters.screenSize;
      return screenSize;
    },
    bannerColor(){
      var bannerColor = 'white';
      
      if(this.pageContent) {
        bannerColor = this.pageContent.banner.images[0].text_color;
      }
      
      return bannerColor;
    },
    bannerAlignment(){
      var bannerAlignment = 'ff-center';
      
      if(this.pageContent) {
        var alignment = this.pageContent.banner.images[0].text_alignment;
        
        switch (alignment) {
          case 'left':
            bannerAlignment = 'ff-text-left ff-valign-top ff-content-top';
            break;
            
          case 'center':
            bannerAlignment = 'ff-center';
            break;
            
          case 'right':
            bannerAlignment = 'ff-text-right ff-valign-bottom ff-content-bottom';
            break;
            
          default:
            bannerAlignment = 'ff-center';
        }
      }
      
      return bannerAlignment;
    },
    bannerText(){
      var bannerText = false;
      
      if(this.pageContent) {
        bannerText = this.pageContent.banner.images[0].text;
      }
      
      return bannerText;
    },
    bannerImage(){
      var bannerImage = {
        desktop: this.banner.size.desktop,
        mobile: this.banner.size.mobile
      };
      
      return bannerImage;
    },
    conditionalRequiredFields(){
      var requiredFields = {
        phone: true,
        email: true,
        contactMethod: false
      };
      
      if( this.form.email && !this.form.phone ) {
        
        requiredFields.phone = false;
        
      } else if( !this.form.email && this.form.phone ) {
        
        requiredFields.email = false;
        
      } else if( this.form.email && this.form.phone ) {
        
        requiredFields.contactMethod = true;
        
      }
      
      return requiredFields;
    },
    meta() {
      var meta = {
        title: this.pageName,
        url: "https://www.csbplab.com.au/contact",
        desc: "",
        image: ""
      }
      
      return meta;
    }
  },
  beforeMount() {
    
    // Check if we have pre-loaded the data server-side
    if(window.__INITIAL_STATE__) {
      var init_state = JSON.parse(window.__INITIAL_STATE__);
      
      if('contact' in init_state.data) {
        this.pageContent = init_state.data.contact;
        
        // Preload and set banner images
        var vm = this;
        var bannerImages = this.pageContent.banner.images[0].sizes;
        
        this.preloadImages([
          bannerImages.desktop,
          bannerImages.mobile
        ]).then(function(imgs) {
          
          vm.banner.size.desktop = imgs[0];
          vm.banner.size.mobile = imgs[1];
        
        // eslint-disable-next-line
        }, function(errImg) {
          // eslint-disable-next-line
          console.log('There was an error loading the images.');
        });
      }
    }
    
    // If not, start loading the data client-side
    if(!this.pageContent) {
      this.loadContent();
    }
    
    // Randomise honeypot
    let randomIds = [ 'middleName', 'url', 'replyToEmail' ];
    let randomNames = [ 'Middle name', 'Url', 'Reply-to email' ];
    let randomIndex = Math.floor(Math.random()*3);
    
    this.form.specialName = randomNames[randomIndex]; 
    this.form.specialId = randomIds[randomIndex]; 
    
    // Get page load datetime (for antispam purposes)
    var d = new Date();
    
    this.form.datetime.load = d.getDate()+' '+this.form.datetime.months[d.getMonth()]+' '+d.getFullYear()+' '+d.getHours()+':'+d.getMinutes()+':'+d.getSeconds();
    
  },
  async mounted() {
    try {
      // Launch Map
      const root_url = "https://"+window.location.hostname;
      const google = await gmapsInit();
      const coords = this.map.coords;
      
      const mapOptions = {
        center: coords,
        zoom: 17,
        styles: this.map.styles,
        disableDefaultUI: true
      }
      
      // eslint-disable-next-line
      const map = new google.maps.Map(document.getElementById("map"), mapOptions);
      
      // Add marker
      // eslint-disable-next-line
      const marker = new google.maps.Marker({
        position: coords,
        map: map,
        icon: {
          url: root_url+"/assets/img/marker-hdpi.png",
          size: new google.maps.Size(80, 80),
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(40, 70),
          scaledSize: new google.maps.Size(80, 80),
        }
      });
      
    } catch (error) {
      // eslint-disable-next-line
      console.error(error);
    }
  },
  methods: {
     async loadContent() {
      // Start loading
      this.loading = true;
      
      // Request data from API
      await this.$axios.get(this.apiURL+'contact').then((response) => {
        this.pageContent = response.data.page;
      // eslint-disable-next-line
      }, (error)  =>  {
        alert("There was an error loading the content for this page. Please reload page to try again.");
      });
      
      // Finish loading
      this.loading = false;
      
      // Preload and set banner images
      var vm = this;
      var bannerImages = this.pageContent.banner.images[0].sizes;
      
      this.preloadImages([
        bannerImages.desktop,
        bannerImages.mobile
      ]).then(function(imgs) {
        
        vm.banner.size.desktop = imgs[0];
        vm.banner.size.mobile = imgs[1];
      
      // eslint-disable-next-line
      }, function(errImg) {
        // eslint-disable-next-line
        console.log('There was an error loading the images.');
      });
      
    },
    preloadImages(srcs) {
      function loadImage(src) {
        
        return new Promise(function(resolve, reject) {
          var img = new Image();
          img.onload = function() {
            resolve(src);
          };
          img.onerror = img.onabort = function() {
            reject(src);
          };
          img.src = src;
        });
        
      }
      
      var promises = [];
      for (var i = 0; i < srcs.length; i++) {
        promises.push(loadImage(srcs[i]));
      }
      
      return Promise.all(promises);
    },
    submitForm() {
      this.form.response.message = "";
      this.form.response.errors = [];
      
      /* 
        Excepted values include:
          value: self-explanatory (string)
          store: if 'true', the field will be stored into the database
          required: if 'true', the form can be submitted if it is empty
          validation: custom validation rule (string)
          
        Honeypot:
          The 'special' field is a honeypot
      */
      
      // Form data container
      var formData = new FormData();
      
      // Form ID
      formData.set('formId', '355');
      
      // Form fields
      formData.set('fields', JSON.stringify({
        firstName: {
          value: this.form.firstName,
          store: true,
          required: true
        },
        lastName: {
          value: this.form.lastName,
          store: true,
          required: true
        },
        email: {
          value: this.form.email,
          store: true,
          required: this.conditionalRequiredFields.email,
          validation: 'email'
        },
        phone: {
          value: this.form.phone,
          store: true,
          required: this.conditionalRequiredFields.phone,
          validation: 'phone'
        },
        message: {
          value: this.form.message,
          store: true,
          required: false
        },
        contactMethod: {
          value: this.form.contactMethod,
          store: true,
          required: this.conditionalRequiredFields.contactMethod
        },
        terms: {
          value: this.form.terms,
          required: true
        },
        special: {
          value: this.form.special,
          required: false,
          validation: 'empty'
        }
      }));
      
      // Timestamps for antispam
      var d = new Date();
    
      this.form.datetime.submit = d.getDate()+' '+this.form.datetime.months[d.getMonth()]+' '+d.getFullYear()+' '+d.getHours()+':'+d.getMinutes()+':'+d.getSeconds();
      
      formData.set('datetime', JSON.stringify({
        start: this.form.datetime.load,
        end: this.form.datetime.submit
      }));
      
      // CSRF Protection Tokens (form will fail without these)
      formData.set('csrf_name', this.$store.state.sessionToken.name);
      formData.set('csrf_value', this.$store.state.sessionToken.value);
      
      // submit to form script
      this.$axios.post(this.apiURL+"forms/enquiry", formData). then((response) => {
            
        this.form.response.status = response.data.status;
        this.form.response.message = response.data.message;
        
        if(response.data.status === 'failure') {
          this.form.response.errors = response.data.error;
          this.form.response.errors = response.data.errors;
        }
        
        if(response.data.status === 'success') {
          this.$gtm.trackEvent({
            'event': 'contact',
            'action': 'submit',
            'enquiry-submit': 'success'
          });
        }
        
        // eslint-disable-next-line
        // console.log(this.form.response);
        
      // eslint-disable-next-line
      }, (error)  =>  {
        
        this.form.response.status = "failure";
        this.form.response.error = "default";
        this.form.response.message = "There was an error sending your email. Please reload page to try again.";
        
        // eslint-disable-next-line
        // console.log(this.form.response);
        
      });
      
      var borderOffset = window.innerWidth >= 768 ? (window.innerWidth * 0.02) + 10 : 10;
      this.$scrollTo('section#form', 500, {offset: -borderOffset});
      
    }
  },
  metaInfo () {
    return {
      title: this.meta.title,
      link: [
        {
          rel: "canonical",
          href: this.meta.url
        }
      ],
      meta: [
        {
          name: "description",
          content: this.meta.desc
        },
        {
          property: "og:title",
          content: this.meta.title + " | CSBP Lab"
        },
        {
          property: "og:url",
          content: this.meta.url
        },
        {
          property: "og:image",
          content: this.meta.image
        },
        {
          property: "og:description",
          content: this.meta.desc
        },
        {
          property: "twitter:title",
          content: this.meta.title + " | CSBP Lab"
        },
        {
          property: "twitter:site",
          content: this.meta.url
        },
        {
          property: "twitter:image.src",
          content: this.meta.image
        },
        {
          property: "twitter:description",
          content: this.meta.desc
        }
      ]
    }
  }
};
</script>